import React, { useEffect, useState } from 'react';
import './Calculations.scss';
import { Box, Container, Header, Link, SpaceBetween } from '@amzn/awsui-components-react/polaris';
import TPENavigator from '../TPENavigator';
import { AppModules } from 'src/models/permissions/RolePermissions';
import { getPermissions } from '../AppPermissions';
import CONSTANTS from 'src/utils/constants';
import { getFeatureFlags, isFeatureEnabledForUser } from 'src/services/FeatureFlagsService';

// Distinct icon per capability. The rounded background is baked INTO the SVG (a <rect>), so the
// colored icon tile needs no CSS. Amber for authoring / reference data, blue for utilities. The
// ICRS card reuses the "create" glyph so it reads as a sibling of Create new calculation.
const AMBER = { bg: '#fff6ea', border: '#f6dcae', fg: '#d17c0a' };
const BLUE = { bg: '#eef6ff', border: '#d6e8fb', fg: '#0972d1' };

const GLYPHS: Record<string, JSX.Element> = {
  create: <><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/><line x1="12" y1="12" x2="12" y2="17"/><line x1="9.5" y1="14.5" x2="14.5" y2="14.5"/></>,
  clone: <><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></>,
  link: <><path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1.5 1.5"/><path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1.5-1.5"/></>,
  cdt: <><rect x="3" y="4" width="18" height="16" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="14.5" x2="21" y2="14.5"/><line x1="9" y1="9" x2="9" y2="20"/><line x1="15" y1="9" x2="15" y2="20"/></>,
  coa: <><rect x="9" y="3" width="6" height="4" rx="1"/><rect x="3" y="17" width="6" height="4" rx="1"/><rect x="15" y="17" width="6" height="4" rx="1"/><path d="M12 7v4M6 17v-2a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v2M12 11v3"/></>,
  tpa: <><path d="M12 3a9 9 0 1 0 9 9h-9z"/><path d="M12 3v9l6.5-6.5A9 9 0 0 0 12 3z"/></>,
  mda: <><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></>,
  testedParty: <><circle cx="6" cy="7" r="3"/><circle cx="18" cy="17" r="3"/><path d="M9 8.5l6 7"/></>,
  bulk: <><path d="M4 6l1.5 1.5L8 5"/><path d="M4 12l1.5 1.5L8 11"/><path d="M4 18l1.5 1.5L8 17"/><line x1="11" y1="6" x2="20" y2="6"/><line x1="11" y1="12" x2="20" y2="12"/><line x1="11" y1="18" x2="20" y2="18"/></>,
  balance: <><line x1="12" y1="3" x2="12" y2="21"/><line x1="7" y1="6" x2="17" y2="6"/><path d="M7 6l-3 6a3 3 0 0 0 6 0z"/><path d="M17 6l-3 6a3 3 0 0 0 6 0z"/><line x1="8" y1="21" x2="16" y2="21"/></>,
};

/**
 * Builds a 44x44 icon whose colored rounded background lives inside the SVG (no CSS tile). When a
 * label is given (e.g. "ICRS") it is baked into the icon as a small pill at the bottom, so the marker
 * is part of the icon rather than a separate badge next to the title.
 */
function buildIcon(glyph: JSX.Element, palette: { bg: string; border: string; fg: string }, label?: string): JSX.Element {
  // With a label, nudge the glyph up so the pill sits below it without overlapping.
  const glyphY = label ? 7 : 10;
  return (
    <svg viewBox="0 0 44 44" width={44} height={44} fill="none" style={{ display: 'block' }} aria-hidden="true" focusable="false">
      <rect x="0.5" y="0.5" width="43" height="43" rx="10" fill={palette.bg} stroke={palette.border}/>
      <g transform={`translate(10,${glyphY})`} stroke={palette.fg} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        {glyph}
      </g>
      {label && (
        <>
          <rect x="7" y="31" width="30" height="10" rx="5" fill="#ffffff" stroke={palette.border}/>
          <text x="22" y="38.5" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif" fontSize="6.5" fontWeight="700" fill={palette.fg}>{label}</text>
        </>
      )}
    </svg>
  );
}

interface CardDef {
  key: string;
  icon: JSX.Element;
  title: string;
  description: string;
  url: string;
  show: boolean;
}

export default function CalculationsLanding() {
  const [ navigateURL, setNavigateURL ] = React.useState('');
  const [testedPartyEnabled, setTestedPartyEnabled] = useState(false);

  useEffect(() => {
      const userAlias = localStorage.getItem('alias') || '';
      getFeatureFlags().then((flags) => {
          setTestedPartyEnabled(isFeatureEnabledForUser(flags, 'testedPartyEnabled', userAlias));
      }).catch(() => {
          setTestedPartyEnabled(false);
      });
  }, []);

  const { canView: canViewCreateNewCalculation } = getPermissions(AppModules.CREATE_NEW_CALCULATION);
  const { canView: canViewCreateNewICRSCalculation } = getPermissions(AppModules.CREATE_NEW_ICRS_CALCULATION);
  const { canView: canViewBulkLink } = getPermissions(AppModules.BULK_LINK);
  const { canView: canViewBulkClone } = getPermissions(AppModules.BULK_CLONE);
  const { canView: canViewCustomDataTables } = getPermissions(AppModules.CUSTOM_DATA_TABLES);
  const { canView: canViewCustomCOA } = getPermissions(AppModules.CUSTOM_COA);
  const { canView: canViewTPAllocation } = getPermissions(AppModules.TP_ALLOCATION);
  const { canView: canViewBulkActions } = getPermissions(AppModules.BULK_ACTIONS);
  const { canView: canViewPeriodCharges } = getPermissions(AppModules.PERIOD_CHARGES);
  const { canView: canViewMultiDimensionalAllocation } = getPermissions(AppModules.MULTIDIMENSIONAL_ALLOCATION);

  // Purpose-based sections. A section renders only when the user can see at least one of its cards,
  // so someone without (say) Bulk Actions and Balance Reconciliation never sees that whole section.
  const sections: { title: string; description: string; cards: CardDef[] }[] = [
    {
      title: 'Create & author calculations',
      description: 'Start a new calculation or build from an existing one.',
      cards: [
        { key: 'create', icon: buildIcon(GLYPHS.create, AMBER), title: 'Create new calculation', description: 'Author a standard Transfer Pricing calculation from scratch.', url: '/calculations/builder/new', show: canViewCreateNewCalculation },
        { key: 'icrs', icon: buildIcon(GLYPHS.create, AMBER, 'ICRS'), title: 'Create ICRS calculation', description: 'Author a calculation for an ICRS transaction.', url: '/icrs-calculations/builder/new', show: canViewCreateNewICRSCalculation },
        { key: 'clone', icon: buildIcon(GLYPHS.clone, AMBER), title: 'Clone calculation', description: 'Clone multiple calculations from a template.', url: '/calculations/clone', show: canViewBulkClone },
        { key: 'link', icon: buildIcon(GLYPHS.link, AMBER), title: 'Link to Template', description: 'Link multiple calculations to a template.', url: '/calculations/link', show: canViewBulkLink },
      ],
    },
    {
      title: 'Reference data & building blocks',
      description: 'Define the shared data and allocations that calculations consume.',
      cards: [
        { key: 'cdt', icon: buildIcon(GLYPHS.cdt, AMBER), title: 'Custom Data Table', description: 'Maintain custom values referenced by calculations, per period.', url: '/calculations/customDataTables', show: canViewCustomDataTables },
        { key: 'coa', icon: buildIcon(GLYPHS.coa, AMBER), title: 'Custom COA definition', description: 'Define custom chart-of-accounts segment values.', url: '/calculations/customCoa', show: canViewCustomCOA },
        { key: 'tpa', icon: buildIcon(GLYPHS.tpa, AMBER), title: 'TP Allocation', description: 'Manage TP allocation worksheets and templates.', url: '/calculations/tpAllocation', show: canViewTPAllocation },
        { key: 'mda', icon: buildIcon(GLYPHS.mda, AMBER), title: 'Multi Dimensional Allocation', description: 'Define multi allocation percentages and calculations.', url: '/calculations/multidimensionalAllocation', show: canViewMultiDimensionalAllocation },
        { key: 'testedParty', icon: buildIcon(GLYPHS.testedParty, AMBER), title: 'Tested Party Mapping', description: 'View and manage CLI-to-Tested-Party mappings.', url: CONSTANTS.PAGE_NAV.TESTED_PARTY_MAPPING.URL, show: testedPartyEnabled },
      ],
    },
    {
      title: 'Month-end & bulk operations',
      description: 'Run high-volume actions and month-end close tasks.',
      cards: [
        { key: 'bulkActions', icon: buildIcon(GLYPHS.bulk, BLUE), title: 'Bulk Actions', description: 'Submit, approve, validate, or create draft for many calculations at once.', url: '/calculations/bulkActions', show: canViewBulkActions },
        { key: 'balanceRecon', icon: buildIcon(GLYPHS.balance, BLUE), title: 'Balance Reconciliation', description: 'Adjust YTD beginning balance for Month End Close.', url: '/calculations/balanceReconciliation', show: canViewPeriodCharges },
      ],
    },
  ];

  const renderCard = (card: CardDef) => (
    <Link key={card.key} onFollow={() => setNavigateURL(card.url)}>
      <div className="calcCard">
        {card.icon}
        <Box variant="strong" color="text-status-info">{card.title}</Box>
        <Box color="text-body-secondary" fontSize="body-s" textAlign="center">{card.description}</Box>
      </div>
    </Link>
  );

  return (
    <SpaceBetween size="l">
      <TPENavigator path={navigateURL}/>
      {sections.map((section) => {
        const visibleCards = section.cards.filter((c) => c.show);
        if (visibleCards.length === 0) {
          return null;
        }
        return (
          <Container key={section.title} header={<Header variant="h2" description={section.description}>{section.title}</Header>}>
            <div className="calcCardGrid">
              {visibleCards.map(renderCard)}
            </div>
          </Container>
        );
      })}
    </SpaceBetween>
  );
}
